import { Box, Chip, Stack, Typography, Tooltip, Skeleton } from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import GroupsIcon from "@mui/icons-material/Groups";
import { motion } from "framer-motion";
import { Icons } from "../../../../assets/icon";
import { useState } from "react";

const EventCard = ({
  event,
  isLoading,
}: {
  event: any;
  isLoading?: boolean;
}) => {
  const [hovered, setHovered] = useState(false);

  // —— Netflix-ish tokens (design only)
  const COLORS = {
    bg: "#0b0b0b",
    panel: "#141414",
    text: "#ffffff",
    muted: "rgba(255,255,255,0.75)",
    border: "rgba(255,255,255,0.12)",
    red: "#E50914",
    redTint: "rgba(229,9,20,0.15)",
  };

  if (isLoading) {
    return (
      <Box sx={{ borderRadius: 3, overflow: "hidden", bgcolor: COLORS.panel }}>
        <Skeleton variant="rectangular" height={220} sx={{ bgcolor: "#1c1c1c" }} />
        <Box sx={{ p: 2 }}>
          <Skeleton width="70%" height={28} sx={{ bgcolor: "#1c1c1c" }} />
          <Skeleton width="50%" height={20} sx={{ mt: 1, bgcolor: "#1c1c1c" }} />
          <Skeleton width="100%" height={18} sx={{ mt: 2, bgcolor: "#1c1c1c" }} />
        </Box>
      </Box>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4 }}
      style={{ height: "100%" }}
    >
      <Box
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        sx={{
          borderRadius: 3,
          overflow: "hidden",
          transition: "transform 0.3s ease, box-shadow 0.3s ease",
          backgroundColor: COLORS.panel,
          border: `1px solid ${COLORS.border}`,
          height: "100%",
          cursor: "pointer",
          display: "flex",
          flexDirection: "column",
          boxShadow: "0 10px 24px rgba(0,0,0,0.35)",
          "&:hover": {
            transform: "translateY(-4px)",
            boxShadow: "0 18px 48px rgba(0,0,0,0.55)",
          },
        }}
      >
        <Box sx={{ position: "relative", overflow: "hidden" }}>
          <Box
            component="img"
            src={event?.primary_thumbnail ?? ""}
            alt={event?.title ?? "Event image"}
            sx={{
              width: "100%",
              height: { xs: 160, sm: 200, md: 220 },
              objectFit: "cover",
              transform: hovered ? "scale(1.04)" : "scale(1)",
              transition: "transform .35s ease",
              display: "block",
              backgroundColor: "#0d0d0d",
            }}
          />
          {/* bottom gradient for cinematic depth */}
          <Box
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              height: "36%",
              background:
                "linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(11,11,11,0.6) 55%, rgba(11,11,11,0.9) 100%)",
              pointerEvents: "none",
            }}
          />
        </Box>

        <Box
          sx={{
            p: { xs: 1.5, sm: 2.5 },
            display: "flex",
            flexDirection: "column",
            flexGrow: 1,
          }}
        >
          <Tooltip title={event?.title ?? ""}>
            <Typography
              fontFamily="Lexend"
              fontWeight={700}
              fontSize={{ xs: "0.98rem", sm: "1.05rem" }}
              lineHeight={1.35}
              color={COLORS.text}
              mb={1}
              sx={{
                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",
                letterSpacing: "-0.01em",
              }}
            >
              {event?.title ?? ""}
            </Typography>
          </Tooltip>

          <Stack
            direction="row"
            spacing={1}
            mb={2}
            sx={{
              flexWrap: "nowrap",
              overflowX: "auto",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": { display: "none" },
            }}
          >
            <Tooltip title="Ticket Price">
              <Chip
                icon={<Icons.Coin height={16} width={16} />}
                label={event?.price ?? "N/A"}
                sx={{
                  fontFamily: "Lexend",
                  backgroundColor: COLORS.redTint,
                  color: COLORS.text,
                  border: `1px solid ${COLORS.red}`,
                  fontWeight: 600,
                  borderRadius: 2,
                  fontSize: "0.75rem",
                  px: 1.2,
                  ".MuiChip-icon": { color: COLORS.text, ml: 0.25 },
                }}
                size="small"
              />
            </Tooltip>

            <Tooltip title="Attendee Limit">
              <Chip
                icon={<GroupsIcon sx={{ fontSize: 18, ml: 0.5, color: COLORS.muted }} />}
                label={event?.venue_size ?? "—"}
                sx={{
                  fontFamily: "Lexend",
                  backgroundColor: "rgba(255,255,255,0.06)",
                  color: COLORS.text,
                  border: `1px solid ${COLORS.border}`,
                  fontWeight: 500,
                  borderRadius: 2,
                  fontSize: "0.75rem",
                  px: 1.2,
                }}
                size="small"
              />
            </Tooltip>

            <Tooltip title="Duration">
              <Chip
                icon={<AccessTimeIcon sx={{ fontSize: 18, ml: 0.5, color: COLORS.muted }} />}
                label={event?.duration ?? "—"}
                sx={{
                  fontFamily: "Lexend",
                  backgroundColor: "rgba(255,255,255,0.06)",
                  color: COLORS.text,
                  border: `1px solid ${COLORS.border}`,
                  fontWeight: 500,
                  borderRadius: 2,
                  fontSize: "0.75rem",
                  px: 1.2,
                }}
                size="small"
              />
            </Tooltip>
          </Stack>

          <Typography
            variant="body2"
            fontFamily="Lexend"
            fontSize={{ xs: "0.78rem", sm: "0.88rem" }}
            color={COLORS.muted}
            lineHeight={1.6}
            sx={{
              display: "-webkit-box",
              WebkitLineClamp: hovered ? "unset" : 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              transition: "all 0.3s ease",
            }}
          >
            {event?.description ?? ""}
          </Typography>
        </Box>
      </Box>
    </motion.div>
  );
};

export default EventCard;
